import { useEffect } from 'react';
import type { RefObject } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Session } from '../../../api/sessionsService';
import type { ForecastAvailability, ActivityAvailability } from '../types';
import type { EventWithCircuit } from '../../../api/eventsService';
import { dateToX } from '../utils/coordinateUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TimeBounds {
  start: Date;
  end: Date;
}

export interface UseTimelineCanvasParams {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  selectedEvent: EventWithCircuit | null;
  sessions: Session[];
  forecastAvailability?: ForecastAvailability;
  radarActivityAvailability?: ActivityAvailability;
  stationActivityAvailability?: ActivityAvailability;
  selectedRange: { start: Date; end: Date } | null;
  selectedPoint: Date | null;
  theme: 'light' | 'dark';
  mode: 'range' | 'point';
  currentTime: Date;
  canvasRenderKey: number;
  /** Visible time window (for zoom/pan). When null, uses event bounds from getEventBounds. */
  viewBounds: TimeBounds | null;
  getEventBounds: () => TimeBounds | null;
  /** Event timezone for calendar-day alignment and label formatting */
  eventTimezone: string;
  /** Compact layout with reduced bar sizes and fonts */
  compact?: boolean;
}

/**
 * Draw day markers at calendar midnight boundaries (in event timezone).
 * This ensures markers scroll with content when panning, and align with sessions/bars.
 */
function drawTimeMarkers(
  ctx: CanvasRenderingContext2D,
  bounds: TimeBounds,
  width: number,
  height: number,
  isDark: boolean,
  eventStartDate: string,
  eventEndDate: string,
  eventTimezone: string,
  compact = false
) {
  const tz = eventTimezone || 'UTC';
  const boundsStart = bounds.start.getTime();
  const boundsEnd = bounds.end.getTime();

  // Parse event dates to get calendar range in event TZ
  const [startY, startM, startD] = eventStartDate.split('-').map(Number);
  const [endY, endM, endD] = eventEndDate.split('-').map(Number);

  const eventStart = dayjs.tz(`${startY}-${String(startM).padStart(2, '0')}-${String(startD).padStart(2, '0')}`, tz).startOf('day');
  const eventEnd = dayjs.tz(`${endY}-${String(endM).padStart(2, '0')}-${String(endD).padStart(2, '0')}`, tz).endOf('day');

  // Draw quarter-day lines (6-hour intervals) at calendar positions
  ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
  ctx.lineWidth = 1;

  let quarterDayCursor = eventStart.startOf('day');
  const lastEventDay = eventEnd.startOf('day');
  while (!quarterDayCursor.isAfter(lastEventDay)) {
    for (const hour of [6, 12, 18]) {
      const dayAtHour = quarterDayCursor.hour(hour).minute(0).second(0).millisecond(0);
      const date = dayAtHour.toDate();
      const dateMs = date.getTime();
      if (dateMs >= boundsStart && dateMs <= boundsEnd) {
        const x = dateToX(date, bounds, width);
        if (x >= 0 && x <= width) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, height - 10);
          ctx.stroke();
        }
      }
    }
    quarterDayCursor = quarterDayCursor.add(1, 'day');
  }

  // Draw day boundary lines at calendar midnights
  ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
  ctx.lineWidth = 1;
  ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
  ctx.font = compact ? '8px sans-serif' : '10px sans-serif';
  ctx.textAlign = 'center';

  let dayBoundaryCursor = eventStart.startOf('day');
  while (!dayBoundaryCursor.isAfter(lastEventDay)) {
    const date = dayBoundaryCursor.toDate();
    const dateMs = date.getTime();
    if (dateMs >= boundsStart && dateMs <= boundsEnd) {
      const x = dateToX(date, bounds, width);
      if (x >= 0 && x <= width) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();

        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
        const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
        if (compact) {
          ctx.fillText(`${dayOfWeek} ${dateLabel}`, x, 7);
        } else {
          ctx.fillText(dayOfWeek, x, 8);
          ctx.fillText(dateLabel, x, 18);
        }
      }
    }
    dayBoundaryCursor = dayBoundaryCursor.add(1, 'day');
  }
}

/**
 * Hook that manages the timeline canvas drawing.
 * Runs a useEffect that draws the full timeline when dependencies change.
 */
export function useTimelineCanvas(params: UseTimelineCanvasParams): void {
  const {
    canvasRef,
    selectedEvent,
    sessions,
    forecastAvailability,
    radarActivityAvailability,
    stationActivityAvailability,
    selectedRange,
    selectedPoint,
    theme,
    mode,
    currentTime,
    canvasRenderKey,
    viewBounds,
    getEventBounds,
    eventTimezone,
    compact = false,
  } = params;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !selectedEvent) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bounds = viewBounds ?? getEventBounds();
    if (!bounds) return;

    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const isDark = theme === 'dark';

    // Clear canvas with background color
    ctx.fillStyle = isDark ? '#141414' : '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Draw past time overlay
    const nowX = dateToX(currentTime, bounds, width);
    if (currentTime > bounds.start) {
      const pastEndX = Math.min(nowX, width);
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(0, 0, pastEndX, height);
    }

    // Draw data availability bars and forecast issuance markers
    const barHeight = compact ? 4 : 8;
    const barSpacing = compact ? 1 : 2;
    let barOffset = height - (compact ? 6 : 10);

    if (stationActivityAvailability?.ranges && stationActivityAvailability.ranges.length > 0) {
      stationActivityAvailability.ranges.forEach((range) => {
        const startX = dateToX(range.start, bounds, width);
        const endX = dateToX(range.end, bounds, width);
        ctx.fillStyle = isDark ? 'rgba(19, 194, 194, 0.4)' : 'rgba(19, 194, 194, 0.3)';
        ctx.fillRect(startX, barOffset, endX - startX, barHeight);
      });
      barOffset -= barHeight + barSpacing;
    }

    if (radarActivityAvailability?.ranges && radarActivityAvailability.ranges.length > 0) {
      radarActivityAvailability.ranges.forEach((range) => {
        const startX = dateToX(range.start, bounds, width);
        const endX = dateToX(range.end, bounds, width);
        ctx.fillStyle = isDark ? 'rgba(255, 77, 79, 0.4)' : 'rgba(255, 77, 79, 0.3)';
        ctx.fillRect(startX, barOffset, endX - startX, barHeight);
      });
      barOffset -= barHeight + barSpacing;
    }

    if (forecastAvailability?.daily && forecastAvailability.daily.length > 0) {
      ctx.strokeStyle = isDark ? 'rgba(250, 173, 20, 0.8)' : 'rgba(250, 173, 20, 0.7)';
      ctx.lineWidth = compact ? 2 : 3;
      forecastAvailability.daily.forEach((issuanceTime) => {
        const x = dateToX(issuanceTime, bounds, width);
        ctx.beginPath();
        ctx.moveTo(x, barOffset);
        ctx.lineTo(x, barOffset + barHeight);
        ctx.stroke();
      });
      barOffset -= barHeight + barSpacing;
    }

    if (forecastAvailability?.hourlyIssuance && forecastAvailability.hourlyIssuance.length > 0) {
      ctx.strokeStyle = isDark ? 'rgba(82, 196, 26, 0.8)' : 'rgba(82, 196, 26, 0.7)';
      ctx.lineWidth = compact ? 2 : 3;
      forecastAvailability.hourlyIssuance.forEach((issuanceTime) => {
        const x = dateToX(issuanceTime, bounds, width);
        ctx.beginPath();
        ctx.moveTo(x, barOffset);
        ctx.lineTo(x, barOffset + barHeight);
        ctx.stroke();
      });
      barOffset -= barHeight + barSpacing;
    }

    // Draw sessions
    if (sessions.length > 0) {
      sessions.forEach((session) => {
        const sessionStartDate = new Date(session.start_time);
        const sessionEndDate = new Date(session.end_time);
        const startX = dateToX(sessionStartDate, bounds, width);
        const endX = dateToX(sessionEndDate, bounds, width);
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)';
        ctx.fillRect(startX, 0, endX - startX, height);
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
        ctx.font = compact ? '9px sans-serif' : '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(session.session_name, (startX + endX) / 2, compact ? 7 : 8);
      });
    }

    // Draw selected range or point
    if (mode === 'range' && selectedRange) {
      const startX = dateToX(selectedRange.start, bounds, width);
      const endX = dateToX(selectedRange.end, bounds, width);
      ctx.fillStyle = 'rgba(24, 144, 255, 0.2)';
      ctx.fillRect(startX, 0, endX - startX, height);
      ctx.strokeStyle = 'rgba(24, 144, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.strokeRect(startX, 0, endX - startX, height);
    } else if (mode === 'point' && selectedPoint) {
      const x = dateToX(selectedPoint, bounds, width);
      ctx.strokeStyle = 'rgba(24, 144, 255, 1)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      ctx.fillStyle = 'rgba(24, 144, 255, 1)';
      ctx.beginPath();
      ctx.arc(x, compact ? 7 : 10, compact ? 4 : 6, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw "now" line indicator
    if (currentTime >= bounds.start && currentTime <= bounds.end) {
      const forecastEndTime = new Date(currentTime.getTime() + 30 * 60 * 1000);
      const forecastEndX = dateToX(forecastEndTime, bounds, width);
      if (forecastEndX > nowX) {
        ctx.fillStyle = isDark ? 'rgba(24, 144, 255, 0.08)' : 'rgba(24, 144, 255, 0.06)';
        const fzTop = compact ? 10 : 15;
        ctx.fillRect(nowX, fzTop, forecastEndX - nowX, height - fzTop - (compact ? 8 : 10));
        const forecastZoneWidth = forecastEndX - nowX;
        if (forecastZoneWidth > 60) {
          ctx.fillStyle = isDark ? 'rgba(24, 144, 255, 0.5)' : 'rgba(24, 144, 255, 0.6)';
          ctx.font = compact ? '7px sans-serif' : '9px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('FORECAST', nowX + forecastZoneWidth / 2, height - (compact ? 8 : 15));
        }
      }
      ctx.strokeStyle = isDark ? 'rgba(255, 77, 79, 0.8)' : 'rgba(255, 77, 79, 0.9)';
      ctx.lineWidth = compact ? 1.5 : 2;
      ctx.setLineDash(compact ? [3, 3] : [4, 4]);
      ctx.beginPath();
      ctx.moveTo(nowX, 0);
      ctx.lineTo(nowX, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = isDark ? 'rgba(255, 77, 79, 0.9)' : 'rgba(255, 77, 79, 1)';
      ctx.font = compact ? 'bold 7px sans-serif' : 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NOW', nowX, compact ? 7 : 10);
    }

    // Draw time markers (at calendar midnights in event timezone)
    drawTimeMarkers(
      ctx,
      bounds,
      width,
      height,
      isDark,
      selectedEvent.start_date,
      selectedEvent.end_date,
      eventTimezone,
      compact
    );

    // Draw bottom border line
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, height - 0.5);
    ctx.lineTo(width, height - 0.5);
    ctx.stroke();
  }, [
    canvasRef,
    selectedEvent,
    sessions,
    forecastAvailability,
    radarActivityAvailability,
    stationActivityAvailability,
    selectedRange,
    selectedPoint,
    theme,
    mode,
    currentTime,
    canvasRenderKey,
    viewBounds,
    getEventBounds,
    eventTimezone,
    compact,
  ]);
}
